import React, { useEffect, useState } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { addLocale } from 'primereact/api';
import { Checkbox } from 'primereact/checkbox';

import { loginZirveNova, getReceiptListZirveNova, sendReceiptZirveNova } from '../module/zirveNova/utils/zirveNovaRequest';
import { translateDocType, documentData, docName, itemNo, checkAmountAndCredit, rowIsTrue, wrongRow } from './eDefterZirveNova/utils/controller';
import { isOther, getErrorReceiptCount, getReceiptDetailsDocType, getCompany } from './eDefterZirveNova/utils/utils';
import receiptCode from '../../assets/data/receiptCode.json';
import SendReceiptDialog from './eDefterZirveNova/components/SendReceiptDialog';

import { formatAmount, formatDate, HeaderProgram, localeDate, mapTipNoToLabel, selectLastThreeMonths, selectLastTwoMonths, PortalHeader, formatDateAndTime } from './utils/utils';
import { InputSwitch } from 'primereact/inputswitch';
import { classNames } from 'primereact/utils';
import Loading from '../Loading';

import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';

// Map ZirveNova BelgeTuru to Luca tip_id
const mapZirveNovaTipToLuca = (zirveNovaTip) => {
    const mapping = {
        AÇILIŞ: 0,
        TAHSIL: 1,
        TEDIYE: 2,
        MAHSUP: 3,
        KAPANIŞ: 4
    };
    return mapping[zirveNovaTip?.toUpperCase()] || 3; // Default to MAHSUP
};

// Transform ZirveNova data to Luca-like structure
const transformZirveNovaToLuca = (zirveNovaData) => {
    if (!zirveNovaData || !zirveNovaData.detay || !Array.isArray(zirveNovaData.detay)) {
        console.warn('Invalid ZirveNova data:', zirveNovaData);
        return null;
    }

    // Handle empty or missing fields with proper defaults for validation
    const getDefaultValue = (value, defaultValue = '') => {
        if (value === null || value === undefined || value === '') {
            return defaultValue;
        }
        return value;
    };

    return {
        // Core fields
        id: zirveNovaData.Id,
        fis_tarihi: getDefaultValue(zirveNovaData.Tarih),
        fis_no: getDefaultValue(zirveNovaData.FisNo),
        fis_state: 1, // Default to active
        tip_id: mapZirveNovaTipToLuca(zirveNovaData.BelgeTuru), // Convert to Luca format for validation
        tip_id_original: getDefaultValue(zirveNovaData.BelgeTuru), // Keep original for display
        borc: Number(zirveNovaData.Borc) || 0,
        alacak: Number(zirveNovaData.Alck) || 0,
        aciklama: getDefaultValue(zirveNovaData.Ack),
        yevmiye: Number(zirveNovaData.YevNo) || 0,
        madde_no: Number(zirveNovaData.YevNo) || 0, // Same as yevmiye for Luca compatibility
        satir_sayisi: Number(zirveNovaData.detay?.length) || 0,
        kaynak_belge_turu: getDefaultValue(zirveNovaData.detay[0]?.Bt_Ad),
        kaynak_belge_turu_aciklama: getDefaultValue(zirveNovaData.detay[0]?.Bt_Ack),
        belgesiz_fis: 1,

        // Receipt details
        receiptDetails:
            zirveNovaData.detay?.map((detail) => ({
                hesap_kodu: getDefaultValue(detail.Hsp_Kod),
                hesap_adi: getDefaultValue(detail.Hsp_Ad),
                borc: Number(detail.TutarB) || 0,
                alacak: Number(detail.TutarA) || 0,
                aciklama: getDefaultValue(detail.Ack),
                evrak_no: getDefaultValue(detail.EvrNo),
                evrak_tarihi: getDefaultValue(detail.EvTarih),
                belge_turu: getDefaultValue(detail.Bt_Ad)
            })) || []
    };
};

export default function ZirveNovaReceiptPage() {
    const [receipts, setReceipts] = useState([]);
    const [originalZirveNovaData, setOriginalZirveNovaData] = useState([]);
    const [expandedRows, setExpandedRows] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const toast = useRef(null);
    const dt = useRef(null);
    const { selectedCompany } = useSelector((state) => state.userCompany);
    const [startDate, setStartDate] = useState(selectLastThreeMonths());
    const [endDate, setEndDate] = useState(selectLastTwoMonths());
    const [loading, setLoading] = useState(false);
    const [selectedCompanyCode, setSelectedCompanyCode] = useState(null);
    const [periodVisible, setPeriodVisible] = useState(false);

    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const [sendReceiptsDialog, setSendReceiptsDialog] = useState(false);

    const [zirveNovaToken, setZirveNovaToken] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);

    const [pending, setPending] = useState(false);
    const [errorReceiptsCount, setErrorReceiptsCount] = useState(0);
    const [checked, setChecked] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(selectLastThreeMonths());
    const [checkErrorReceipts, setCheckErrorReceipts] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const { isAuthenticatedWithVkn, taxNumber } = useSelector((state) => state.auth);

    addLocale('tr', localeDate);

    useEffect(() => {
        setCompanies(selectedCompany.modules.find((module) => module?.setting?.settings?.program?.title?.includes('Zirve'))?.setting?.settings?.program?.zirveNovaCompanies);
    }, [selectedCompany]);

    useEffect(() => {
        if (receipts.length !== 0) setErrorReceiptsCount(getErrorReceiptCount(receipts));
    }, [receipts]);

    useEffect(() => {
        if (errorReceiptsCount === 0) {
            setCheckErrorReceipts(false);
        }
    }, [errorReceiptsCount]);

    const getZirveNovaReceiptList = async () => {
        setLoading(true);
        let selectedModule = selectedCompany.modules.find((module) => module?.setting?.settings?.program?.title?.includes('Zirve'));

        if (!selectedModule) {
            setTimeout(() => {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: 'ZirveNova modülü bulunamadı.', life: 3000 });
            }, 100);
            setLoading(false);
            return;
        }

        const req = selectedModule?.setting?.settings?.program?.requirements;

        try {
            // Login to ZirveNova
            const loginRes = await loginZirveNova(req[0].username, req[0].password);
            const token = loginRes.data.data;
            setZirveNovaToken(token);

            // Get receipt list - ZirveNova uses yyyy-mm-dd format
            const receiptsRes = await getReceiptListZirveNova(String(selectedCompanyId), formatDate(startDate, 'dotReverse'), formatDate(endDate, 'dotReverse'), token.access_token);

            // Store original ZirveNova data for sending
            setOriginalZirveNovaData(receiptsRes.data.data.liste);

            // Transform data ONLY for error checking and UI display
            const transformedReceipts = receiptsRes.data.data.liste
                .map((zirveNovaReceipt) => {
                    const transformedReceipt = transformZirveNovaToLuca(zirveNovaReceipt);
                    return transformedReceipt ? { receipt: transformedReceipt } : null;
                })
                .filter(Boolean);

            setReceipts(transformedReceipts);

            setTimeout(() => {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Fişler başarıyla getirildi.', life: 3000 });
            }, 1);
        } catch (error) {
            setLoading(false);
            setTimeout(() => {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
            }, 1);
            return;
        }
        setLoading(false);
    };

    if (loading) {
        return <Loading loading={loading} onChangeLoading={() => setLoading(false)} />;
    }

    const handleMonthChange = (e) => {
        setSelectedMonth(e.value);
        setStartDate(new Date(e.value.getFullYear(), e.value.getMonth(), 1));
        setEndDate(new Date(e.value.getFullYear(), e.value.getMonth() + 1, 0));
    };

    const rowExpansionTemplate = (rowData) => {
        const receiptDetails = rowData.receipt.receiptDetails;
        const tip_id = rowData.receipt.tip_id;
        const kaynak_belge_turu_aciklama = rowData.receipt.kaynak_belge_turu_aciklama;
        const belgesizFis = rowData.receipt.belgesiz_fis === 1;
        return (
            <div className="p-3">
                <h5>Fiş Detayı</h5>
                <DataTable emptyMessage="Detay Bulunamadı" value={receiptDetails}>
                    <Column field="hesap_kodu" header="Hesap Kodu" style={{ minWidth: '12rem' }} sortable></Column>
                    <Column field="hesap_adi" header="Hesap Adı" style={{ minWidth: '12rem' }} sortable></Column>
                    <Column field="borc" header="Borç" style={{ minWidth: '12rem' }} sortable body={(rowData) => checkAmountAndCredit(rowData.borc, rowData.alacak, 'borc')}></Column>
                    <Column field="alacak" header="Alacak" style={{ minWidth: '12rem' }} sortable body={(rowData) => checkAmountAndCredit(rowData.borc, rowData.alacak, 'alacak')}></Column>
                    <Column field="aciklama" header="Açıklama" style={{ minWidth: '12rem' }} sortable></Column>
                    <Column field="evrak_no" header="Evrak No" style={{ minWidth: '12rem' }} sortable body={(rowData) => documentData(rowData.evrak_no, rowData.evrak_tarihi, 'evrak_no', kaynak_belge_turu_aciklama, tip_id, belgesizFis)}></Column>
                    <Column
                        field="evrak_tarihi"
                        header="Evrak Tarihi"
                        style={{ minWidth: '12rem' }}
                        sortable
                        body={(rowData) => formatDateAndTime(documentData(rowData.evrak_no, rowData.evrak_tarihi, 'evrak_tarihi', kaynak_belge_turu_aciklama, tip_id, belgesizFis))}
                    ></Column>
                    <Column field="belge_turu" header="Belge Türü" style={{ minWidth: '12rem' }} sortable body={(rowData) => getReceiptDetailsDocType(rowData.belge_turu)}></Column>
                </DataTable>
            </div>
        );
    };

    const docTemplate = (rowData, title) => {
        const doc = rowData?.receipt?.receiptDetails[0];
        const belgesizFis = rowData?.receipt?.belgesiz_fis === 1;
        return documentData(doc?.evrak_no, doc?.evrak_tarihi, title, rowData?.receipt?.kaynak_belge_turu_aciklama, rowData?.receipt?.tip_id, belgesizFis);
    };

    // Send receipts function adapted for ZirveNova
    const sendReceipts = async (enteredBy, vknTckn, startDate, endDate, directSendValue) => {
        let module = selectedCompany.modules.find((module) => module?.setting?.settings?.program?.title?.includes('Zirve'));
        const zirveNovaCompany = module?.setting?.settings?.program?.zirveNovaCompanies?.filter((company) => company.companyCode === selectedCompanyId)[0];
        const webServiceReq = zirveNovaCompany.webService.requirements;

        const data = {
            token: zirveNovaToken.access_token,
            companyId: selectedCompanyId,
            startDate: formatDate(startDate, 'dotReverse'),
            endDate: formatDate(endDate, 'dotReverse'),
            enteredBy: enteredBy,
            eDefterSubeAdi: zirveNovaCompany.eDefterSubeAdi,
            eDefterSubeKodu: zirveNovaCompany.eDefterSubeKodu,
            VknTckn: vknTckn,
            webServiceUrl: zirveNovaCompany.webService.url,
            webServiceUsername: webServiceReq.username,
            webServicePassword: webServiceReq.password,
            data: originalZirveNovaData,
            directSend: directSendValue
        };

        try {
            setPending(true);
            const res = await sendReceiptZirveNova(data);
            setPending(false);
            setSendReceiptsDialog(false);
            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: res.data.message, life: 3000 });

            setTimeout(() => {
                getReceiptListZirveNova(selectedCompanyId, formatDate(startDate, 'dotReverse'), formatDate(endDate, 'dotReverse'), zirveNovaToken);
            }, 3500);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || 'Bir hata oluştu', life: 3000 });
            setPending(false);
        }
    };

    const header = (
        <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="flex align-items-center gap-5">
                <h4 className="m-0">Zirve Nova Fiş Listesi</h4>
                {receipts.length !== 0 && (
                    <div className="flex align-items-center gap-5">
                        <div className="m-0 p-badge p-overlay-badge p-badge-info">
                            Hatalı Fiş Sayısı <span className="p-badge p-badge-danger">{errorReceiptsCount}</span>
                        </div>
                        <div className=" flex align-items-end gap-2">
                            <label className="block font-normal">Hatalı Fişleri Listele</label>
                            <InputSwitch
                                checked={checkErrorReceipts}
                                onChange={(e) => {
                                    setCheckErrorReceipts(e.value);
                                    setSelectedReceipt(null);
                                }}
                                disabled={errorReceiptsCount === 0}
                            />
                        </div>
                        <div className="p-badge p-overlay-badge p-badge-success text-white">
                            Fiş Adedi <span className="p-badge p-badge-danger">{receipts.length}</span>
                        </div>
                    </div>
                )}
            </div>
            <div>
                {receipts.length !== 0 && (
                    <h6 className="mb-0 mr-6 p-badge p-overlay-badge p-badge-danger text-white h-auto w-auto">
                        Toplam Borç{' '}
                        {formatAmount(
                            receipts.reduce((acc, item) => acc + item.receipt.borc, 0),
                            'TRY'
                        )}
                    </h6>
                )}
                {receipts.length !== 0 && (
                    <h6 className="m-0 p-badge p-overlay-badge p-badge-success text-white h-auto w-auto">
                        Toplam Alacak{' '}
                        {formatAmount(
                            receipts.reduce((acc, item) => acc + item.receipt.alacak, 0),
                            'TRY'
                        )}
                    </h6>
                )}
            </div>
            <div>
                <Button icon="pi pi-arrow-right" severity="success" className="mr-4 text-white" label="E-Defter Oluştur" onClick={() => setSendReceiptsDialog(true)} disabled={!(receipts.length > 0 && errorReceiptsCount === 0)} />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Ara..." />
                </span>
            </div>
        </div>
    );

    return (
        <div>
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between">
                <HeaderProgram selectedCompany={selectedCompany} moduleName="ZirveNova" />
            </div>
            <h3 className="mb-5">Zirve Nova Fiş Tablosu</h3>
            <div className="card flex align-items-end justify-content-between flex-wrap py-4">
                <div className="flex align-items-end gap-5">
                    <div className="w-17rem relative">
                        <label className="mb-2 block">Şirketler</label>
                        <Dropdown
                            dataKey="companyCode"
                            value={selectedCompanyId}
                            onChange={(e) => setSelectedCompanyId(e.value)}
                            options={companies}
                            optionLabel="companyName"
                            optionValue="companyCode"
                            placeholder="Şirket Seçiniz"
                            filter
                            className={classNames({ 'p-invalid': !selectedCompanyId, 'w-full': true })}
                            emptyMessage="Şirket Bulunamadı"
                        />
                        {!selectedCompanyId && (
                            <small className="p-error absolute left-0" style={{ bottom: '-18px' }}>
                                Şirket Seçilmedi
                            </small>
                        )}
                    </div>

                    <div>
                        <label className="mb-2 block">Parçalı Defter</label>
                        <InputSwitch checked={checked} onChange={(e) => setChecked(e.value)} />
                    </div>
                    {checked ? (
                        <>
                            <div className="w-17rem">
                                <label className="block mb-2" htmlFor="cal_date">
                                    Başlangıç Tarihi
                                </label>
                                <Calendar className="w-full" value={startDate} dateFormat="dd/mm/yy" locale="tr" onChange={(e) => setStartDate(e.value)} showIcon placeholder="Lütfen başlangıç tarihi seçiniz" />
                            </div>
                            <div>
                                <label className="block mb-2" htmlFor="cal_date">
                                    Bitiş Tarihi
                                </label>
                                <Calendar value={endDate} dateFormat="dd/mm/yy" locale="tr" onChange={(e) => setEndDate(e.value)} showIcon placeholder="Lütfen bitiş tarihi seçiniz" />
                            </div>
                        </>
                    ) : (
                        <div className="w-17rem">
                            <label className="mb-2 block">Defter Dönemi</label>
                            <Calendar className="w-full" value={selectedMonth} view="month" dateFormat="mm/yy" locale="tr" onChange={handleMonthChange} showIcon placeholder="Lütfen bir ay seçiniz" />
                        </div>
                    )}
                    <Button icon="pi pi-ticket" onClick={() => getZirveNovaReceiptList()} className="px-4" label="Fiş Getir" disabled={!(selectedCompanyId && startDate && endDate)} />
                </div>
                <div className="flex align-items-end gap-5">
                    <PortalHeader selectedCompany={selectedCompany} selectedCompanyCode={selectedCompanyId} moduleName="ZirveNova" />
                </div>
            </div>
            {errorReceiptsCount !== 0 && (
                <Message
                    className="mb-3 h-4rem w-full flex justify-content-start font-semibold"
                    style={{ backgroundColor: '#FC6161' }}
                    sticky
                    severity="error"
                    text="Hatalı fiş kayıtlarını görüntülemek için 'Hatalı Fişleri Listele' butonunu aktif hale getirmelisiniz."
                    closable
                />
            )}
            <div className="card">
                <DataTable
                    ref={dt}
                    value={checkErrorReceipts ? receipts.filter((receipt) => wrongRow(receipt)) : receipts}
                    dataKey="receipt.id"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 100, 200]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{totalRecords} fişten {first} ila {last} arası gösteriliyor"
                    globalFilter={globalFilter}
                    header={header}
                    emptyMessage="Fiş Bulunamadı"
                    rowExpansionTemplate={rowExpansionTemplate}
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    className="p-datatable-gridlines"
                    selectionMode={'checkbox'}
                    selection={selectedReceipt}
                    onSelectionChange={(e) => setSelectedReceipt(e.value)}
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                    <Column expander style={{ width: '5rem' }} />
                    <Column field="" header="" style={{ minWidth: '2rem' }} body={(rowData) => rowIsTrue(rowData)}></Column>
                    <Column field="receipt.fis_tarihi" header="Fiş Tarihi" sortable style={{ minWidth: '12rem' }} body={(rowData) => formatDateAndTime(rowData.receipt.fis_tarihi)}></Column>
                    <Column field="receipt.fis_no" header="Fiş No" sortable style={{ minWidth: '8rem' }}></Column>
                    <Column field="receipt.yevmiye" header="Yevmiye Madde No" sortable style={{ minWidth: '10rem' }} body={(rowData) => rowData.receipt.yevmiye}></Column>
                    <Column field="receipt.tip_id" header="Fiş Türü" body={(rowData) => rowData.receipt.tip_id_original || rowData.receipt.tip_id} sortable style={{ minWidth: '12rem' }}></Column>
                    <Column field="receipt.borc" header="Toplam Borç" sortable style={{ minWidth: '12rem' }} body={(rowData) => formatAmount(rowData.receipt.borc, 'TRY')}></Column>
                    <Column field="receipt.alacak" header="Toplam Alacak" sortable style={{ minWidth: '12rem' }} body={(rowData) => formatAmount(rowData.receipt.alacak, 'TRY')}></Column>
                    <Column field="receipt.aciklama" header="Açıklama" sortable style={{ minWidth: '12rem' }}></Column>
                    <Column
                        field="receipt.kaynak_belge_turu"
                        header="Kaynak Belge Türü"
                        sortable
                        style={{ minWidth: '12rem' }}
                        body={(rowData) => translateDocType(rowData.receipt.kaynak_belge_turu, rowData.receipt.tip_id, rowData.receipt.belgesiz_fis === 1)}
                    ></Column>
                    <Column
                        field="receipt.kaynak_belge_turu_aciklama"
                        header="Kaynak Belge Türü Açıklama"
                        sortable
                        style={{ minWidth: '12rem' }}
                        body={(rowData) => docName(rowData.receipt.kaynak_belge_turu_aciklama, rowData.receipt.kaynak_belge_turu, rowData.receipt.tip_id, rowData.receipt.belgesiz_fis === 1)}
                    ></Column>

                    <Column field="receipt.receiptDetails[0].evrak_no" header="Evrak No" style={{ minWidth: '12rem' }} sortable body={(rowData) => docTemplate(rowData, 'evrak_no')}></Column>

                    <Column field="receipt.receiptDetails[0].evrak_tarihi" header="Evrak Tarihi" style={{ minWidth: '12rem' }} sortable body={(rowData) => formatDateAndTime(docTemplate(rowData, 'evrak_tarihi'))}></Column>
                </DataTable>
            </div>

            <SendReceiptDialog
                selectedStartDate={startDate}
                receipts={receipts}
                sendReceiptsDialog={sendReceiptsDialog}
                onCloseDialog={() => setSendReceiptsDialog(false)}
                sendReceipts={sendReceipts}
                pending={pending}
                selectedCompanyId={selectedCompanyId}
            />
        </div>
    );
}
